

// Recursive mobile menu item component
import React, { useEffect, useRef, useState } from "react";
import { SafeLink } from "../../ui/SafeLink";
import { useNavigation } from "../../hooks/useNavigation";
import { AnimatePresence, motion } from "framer-motion";
import Hamburger from "hamburger-react";
import { ChevronDown } from "lucide-react";
import SignInOut from "../singinout/signinout";
import type { NavigationLink } from "@/models/navigation";

function MobileMenuItem({
    item,
    openKey,
    setOpenKey,
    level = 0,
    onCloseMenu,
}: {
    item: NavigationLink;
    openKey: string | null;
    setOpenKey: (key: string | null) => void;
    level?: number;
    onCloseMenu: () => void;
}) {
    const hasSublinks = item.sublinks && item.sublinks.length > 0;
    const isOpen = openKey === item.name;
    const padding = level === 0 ? "" : `pl-${Math.min(level * 4, 8)}`;
    return (
        <li className={`w-full ${padding}`}>
            {hasSublinks ? (
                <>
                    <button
                        className="footer-link flex items-center justify-between w-full px-0 py-2 text-base font-normal text-neutral-200 hover:text-harvey-yellow transition-colors duration-200"
                        onClick={() => setOpenKey(isOpen ? null : item.name)}
                        aria-expanded={isOpen ? "true" : "false"}
                        type="button"
                    >
                        <span className="flex gap-1">{item.name}</span>
                        <ChevronDown size={20} color="white" className={isOpen ? "rotate-180" : ""} />
                    </button>
                    {isOpen && (
                        <ul className="ml-4">
                            {item.sublinks
                                // .sort((a, b) => a.name.localeCompare(b.name))
                                .map((sub: NavigationLink) => (
                                    <MobileMenuItem
                                        key={sub.name}
                                        item={sub}
                                        openKey={openKey}
                                        setOpenKey={setOpenKey}
                                        level={level + 1}
                                        onCloseMenu={onCloseMenu}
                                    />
                                ))}
                        </ul>
                    )}
                </>
            ) : (
                <SafeLink
                    onClick={onCloseMenu}
                    className="footer-link flex items-center w-full px-0 py-2 text-base font-normal text-neutral-200 hover:text-harvey-yellow transition-colors duration-200"
                    to={item.url || "/"}
                >
                    <span className="flex gap-1">{item.name}</span>
                </SafeLink>
            )}
        </li>
    );
}


export function HamburgerMenu() {

    const [isOpen, setOpen] = useState(false);
    const ref = useRef(null);
    const [siteNavigation, setSiteNavigation] = useState<NavigationLink[]>([]);
    const [openKey, setOpenKey] = useState<string | null>(null);
    const { data: nav } = useNavigation()

    // lock body scroll while menu is open
   useEffect(() => {
     const prev = document.body.style.overflow;
     document.body.style.overflow = isOpen ? 'hidden' : prev;
    return () => { document.body.style.overflow = prev; };
   }, [isOpen]);


    // Normalize and cast to NavigationLink recursively
    const normalizeLink = React.useCallback((item: any): NavigationLink => {
        return {
            ...item,
            sublinks: Array.isArray(item.sublinks)
                ? item.sublinks.map(normalizeLink)
                : [],
        };
    }, []);

    useEffect(() => {
        if (nav && Array.isArray(nav.results) && nav.results.length > 0) {
            const sortedNav: NavigationLink[] = nav.results.map((item: any) => {
                let normalized = normalizeLink(item);
                if (normalized.name.toLowerCase() === 'about us') {
                    normalized = { ...normalized, url: '/about-us/' };
                } else if (normalized.name.toLowerCase() === 'contact us') {
                    normalized = { ...normalized, url: '/contact-us/' };
                } else if (normalized.name.toLowerCase() === 'resources') {
                    normalized = { ...normalized, url: '/resources' };
                } else if (normalized.name.toLowerCase() === 'showroom tour') {
                    normalized = { ...normalized, url: '/showroom/' };
                }
                return normalized;
            });
            // setSiteNavigation(sortedNav);
            setSiteNavigation(
                sortedNav.filter(item => item.name.toLowerCase() !== "products") // <-- filter out Products
            );
        }
    }, [nav, normalizeLink]);


    const handleCloseMenu = () => setOpen(false);

    return (
        <div ref={ref} className="bg-harvey-black fixed right-2 top-2">
            <div className="flex justify-end items-center bg-black">
                <Hamburger hideOutline distance="lg" color="white" rounded={true} toggled={isOpen} size={30} toggle={setOpen} direction={"right"} />
            </div>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed left-0 shadow-4xl w-full right-0 top-16 py-8 pl-4 pt-0 min-h-screen max-h-[100vh] bg-black z-50 rounded-b overflow-y-auto overflow-x-hidden"
                    >
                        <ul className="grid gap-2 z-50 overflow-y-scroll overflow-x-hidden my-4">
                            {siteNavigation.map((route) => (
                                <MobileMenuItem
                                    key={route.name}
                                    item={route}
                                    openKey={openKey}
                                    setOpenKey={setOpenKey}
                                    onCloseMenu={handleCloseMenu}
                                />
                            ))}
                            <li className="w-full mt-4">                                
                                <div className="footer-link flex items-center w-full px-0 py-2 text-base font-normal text-neutral-200 hover:text-harvey-yellow transition-colors duration-200">
                                    <SignInOut />
                                </div>
                            </li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
